import { DragHandleIcon } from '@chakra-ui/icons'
import { Box, Button, Flex, Text, Wrap, WrapItem } from '@chakra-ui/react'
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { DeleteButton } from '../../Buttons/DeleteButton'

// Ordered, reorderable selection of report columns. Columns are added by clicking one of the
// remaining-column buttons, reordered by dragging, and removed individually.
export function ColumnSelector<T extends string>({
  title,
  options,
  getLabel,
  selected,
  onChange,
  itemName = 'column',
}: {
  title: string
  options: readonly T[]
  getLabel: (value: T) => string
  selected: T[]
  onChange: (next: T[]) => void
  itemName?: string
}) {
  const remaining = options.filter(option => !selected.includes(option))

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return
    const next = [...selected]
    const [moved] = next.splice(result.source.index, 1)
    next.splice(result.destination.index, 0, moved)
    onChange(next)
  }

  return (
    <Box>
      <Text fontWeight="500" fontSize="sm" color="gray.700" mb={1}>
        {title}
      </Text>
      {remaining.length > 0 && (
        <Box>
          <Text fontSize="sm" color="gray.500" fontWeight={600} mb={1}>
            Add column
          </Text>
          <Wrap spacing={1} mb={3}>
            {remaining.map(option => (
              <WrapItem key={option}>
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="green"
                  onClick={() => onChange([...selected, option])}>
                  {getLabel(option)}
                </Button>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={`columns-${title}`}>
          {provided => (
            <Flex direction="column" ref={provided.innerRef} {...provided.droppableProps}>
              {selected.length === 0 && (
                <Text fontStyle="italic" color="gray.500" fontSize="sm">
                  No {itemName}s selected
                </Text>
              )}
              {selected.map((column, index) => (
                <Draggable key={column} draggableId={column} index={index}>
                  {(dragProvided, snapshot) => (
                    <Box
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      mb={1}
                      bg="white"
                      borderWidth={1}
                      borderRadius="md"
                      boxShadow={snapshot.isDragging ? 'md' : 'sm'}>
                      <Flex pr={1} align="center">
                        <Flex {...dragProvided.dragHandleProps} p={2} cursor="grab">
                          <DragHandleIcon color="gray.500" w={3} h={3} />
                        </Flex>
                        <Text flex={1} minW={0} fontSize="sm">
                          {getLabel(column)}
                        </Text>
                        <DeleteButton
                          size="xs"
                          noConfirm
                          itemName={getLabel(column)}
                          onDelete={() => onChange(selected.filter(c => c !== column))}
                        />
                      </Flex>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Flex>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  )
}
